import { ReloadOutlined, WarningOutlined } from '@ant-design/icons'
import { useMutation, useQuery } from '@apollo/client'
import {
  type EltModelFieldsFragment,
  FetchConnectionsDocument,
  FetchEltModelsDocument,
  FetchSyncSuccessfulModelsDocument,
  InvalidateEltModelSyncDocument,
} from '@salescore/client-api'
import { getOrganizationIdFromPath } from '@salescore/client-base'
import { SourceProviderIcon } from '@salescore/client-common'
import { datetimeFormatJaYYYYMMDDHHmm, getColumnSearchProps } from '@salescore/frontend-common'
import { App, Button, Card, Table, Tag, Typography } from 'antd'
import { Array, Order, pipe, Record, String } from 'effect'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import type { ReactElement } from 'react'

import { ModelLabel } from './ModelLabel'
import { PropertyButton } from './PropertyButton'

const { Text } = Typography

export const SourceModelTable = ({
  connectionId,
  redirectToEltModel,
}: {
  connectionId: string | undefined
  redirectToEltModel: (eltModelName: string) => void
}): ReactElement => {
  const { modal, message } = App.useApp()

  const { data: syncSuccessfulModelsResultData } = useQuery(FetchSyncSuccessfulModelsDocument, {
    variables: { organizationId: getOrganizationIdFromPath() },
  })

  const { data, refetch, loading } = useQuery(FetchEltModelsDocument, {
    variables: {
      organizationId: getOrganizationIdFromPath(),
    },
  })
  const connections =
    useQuery(FetchConnectionsDocument, {
      variables: {
        organizationId: getOrganizationIdFromPath(),
      },
    }).data?.connections ?? []
  const [invalidateEltModelSync] = useMutation(InvalidateEltModelSyncDocument)

  const connectionMapper = Record.fromIterableBy(Array.reverse(connections), (c) => c.id)
  const eltModels = data?.eltModels ?? []
  const sortedEltModels = pipe(
    eltModels,
    Array.filter((x) => x.type === 'source'),
    Array.sortBy(
      Order.mapInput(
        Order.Tuple([
          Order.make<string | null | undefined>((a, b) =>
            a === b
              ? 0
              : // eslint-disable-next-line sonarjs/no-nested-conditional
                a === null || a === undefined
                ? -1
                : // eslint-disable-next-line sonarjs/no-nested-conditional
                  b === null || b === undefined
                  ? 1
                  : String.localeCompare(b)(a),
          ),
          Order.make<string>((a, b) => String.localeCompare(b)(a)),
          Order.make<string>((a, b) => String.localeCompare(b)(a)),
        ]),
        (x) => [connectionMapper[x.connectionId ?? '']?.source.provider, x.connectionId ?? '', x.name] as const,
      ),
    ),
  )

  const syncSuccessfulModels = syncSuccessfulModelsResultData?.syncSuccessfulModels ?? []
  const allConnectionIds = pipe(
    eltModels,
    Array.map((x) => x.connectionId),
    Array.filter(isNotNullish),
    Array.dedupe,
  )

  return (
    <Card>
      <Table
        loading={loading}
        dataSource={sortedEltModels}
        pagination={{
          pageSize: 100,
        }}
        scroll={{ y: 'min(85vh, calc(100vh - 250px))' }}
        columns={[
          {
            title: t(`連携先`),
            key: `connection`,
            width: 200,
            filters: connections
              .filter((x) => allConnectionIds.includes(x.id))
              .map((x) => {
                const source = connectionMapper[x.id]?.source
                return {
                  value: x.id,
                  text: (
                    <span>
                      <SourceProviderIcon provider={source?.provider} /> {source?.name}
                    </span>
                  ),
                }
              })
              .filter(isNotNullish),
            onFilter: (value: unknown, record: EltModelFieldsFragment) => record.connectionId === value,
            defaultFilteredValue: [connectionId].filter((x) => x !== undefined && x !== '').filter(isNotNullish),
            render: (_: unknown, eltModel: EltModelFieldsFragment): ReactElement => {
              const source = connectionMapper[eltModel.connectionId ?? '']?.source
              if (source === undefined) {
                return <></>
              }
              return (
                <span>
                  <SourceProviderIcon provider={source.provider} /> {source.name}
                </span>
              )
            },
          },
          {
            title: t(`オブジェクト名`),
            width: 250,
            key: `label`,
            sorter: Order.mapInput(
              Order.make<string>((a, b) => String.localeCompare(b)(a)),
              (x: EltModelFieldsFragment) => x.model.label,
            ),
            ...getColumnSearchProps<EltModelFieldsFragment>((eltModel) => eltModel.model.label),
            render: (_, eltModel) => <ModelLabel model={eltModel.model} />,
          },
          {
            title: t(`内部名`),
            width: 250,
            key: `name`,
            sorter: Order.mapInput(
              Order.make<string>((a, b) => String.localeCompare(b)(a)),
              (x: EltModelFieldsFragment) => x.name,
            ),
            ...getColumnSearchProps<EltModelFieldsFragment>((eltModel) => eltModel.name),
            render: (_, eltModel) => <Tag>{eltModel.name}</Tag>,
          },
          {
            title: t(`同期時刻`),
            // 厳密には「同期時刻」はsyncedAtが正しく、syncedAtとtransformedAtを別々に表示したいが、
            // ユーザーがそこまで理解できないので、transformedAtを表示する
            key: `transformedAt`,
            width: 200,
            sorter: Order.mapInput(
              Order.make<string | null | undefined>((a, b) =>
                // eslint-disable-next-line sonarjs/no-nested-conditional
                isNotNullish(a) ? (isNotNullish(b) ? String.localeCompare(b)(a) : -1) : isNotNullish(b) ? 1 : 0,
              ),
              (x: EltModelFieldsFragment) => x.transformedAt,
            ),

            render: (_: unknown, eltModel: EltModelFieldsFragment): ReactElement => {
              if (eltModel.transform?.type === 'view') {
                // eslint-disable-next-line sonarjs/todo-tag
                // TODO: 同期が不要なことをどう伝えればいいか？
                return <span className="text-sm text-gray-400">同期の必要はありません</span>
              }
              if (isNotNullish(eltModel.syncInvalidatedAt) || isNotNullish(eltModel.transformInvalidatedAt)) {
                return <span>{t(`再同期中`)}</span>
              }
              return (
                <span>
                  {!syncSuccessfulModels.includes(eltModel.name) && (
                    <Text type={'danger'}>
                      <WarningOutlined />
                    </Text>
                  )}
                  {datetimeFormatJaYYYYMMDDHHmm(eltModel.transformedAt)}
                </span>
              )
            },
          },
          {
            title: ``,
            key: `properties`,
            width: 100,
            render: (_, eltModel) => <PropertyButton eltModel={eltModel} />,
          },
          {
            title: ``,
            key: `form`,
            width: 150,
            render: (_: unknown, eltModel: EltModelFieldsFragment): ReactElement => {
              const disabled =
                eltModel.model.creatable === false ||
                eltModel.model.updatable === false ||
                eltModel.model.write === undefined
              return (
                <Button
                  type="text"
                  className="text-blue-500"
                  icon={<ReloadOutlined />}
                  disabled={disabled}
                  onClick={() => {
                    redirectToEltModel(eltModel.name)
                  }}
                >
                  {t(`入力項目設定`)}
                </Button>
              )
            },
          },
          {
            title: ``,
            key: `invalidate`,
            width: 100,
            render: (_: unknown, eltModel: EltModelFieldsFragment): ReactElement => {
              const disabled =
                eltModel.syncedAt === undefined ||
                eltModel.syncedAt === null ||
                eltModel.transformedAt === undefined ||
                eltModel.transformedAt === null
              return (
                <Button
                  type="text"
                  className="text-blue-500"
                  icon={<ReloadOutlined />}
                  disabled={disabled}
                  onClick={() => {
                    void modal.confirm({
                      content: (
                        <div>
                          {t(`「{{label}}」のすべてのレコードを再取得します。`, { label: eltModel.model.label })}
                          {t(`通常、この操作を行う必要はありません。`)}
                          {t(`同期結果に問題が発生した際のみ、この操作を行ってください。`)}
                          {t(`同期には時間がかかることがあります。`)}
                        </div>
                      ),
                      cancelText: t('いいえ'),
                      okText: t(`はい`),
                      onOk: () => {
                        // eslint-disable-next-line sonarjs/todo-tag
                        // TODO: この部分はシートの同期の仕組に変更したほうが良い
                        void invalidateEltModelSync({
                          variables: {
                            organizationId: getOrganizationIdFromPath(),
                            id: eltModel.id,
                          },
                          onCompleted: (): void => {
                            void message.success(
                              t(`再取得をセットしました。次回同期時に、全てのレコードが再取得されます`),
                            )
                            void refetch()
                          },
                          onError: (): void => {
                            void message.error(t(`エラーが発生しました`))
                          },
                        })
                      },
                    })
                  }}
                >
                  {t(`再同期`)}
                </Button>
              )
            },
          },
        ]}
      />
    </Card>
  )
}
